/**
 * Answer markdown -> React elements. Text only: there is no HTML path at all (no `dangerouslySetInnerHTML`,
 * no raw-HTML passthrough), so `<script>` or `<img onerror>` in model or document text is just visible text.
 *
 * Supported: headings, paragraphs, bullet/numbered lists, block quotes, fenced code, rules, GFM tables,
 * **bold**, *italic*, `code`, [links](https://…) (http/https only), `¶pid` references (jump buttons) and
 * `[S1]` citation markers. `[[download:…]]` lines (web-app artifacts) are dropped.
 */

import { Fragment, type ReactNode, useMemo } from "react";
import type { JobCitation } from "../backends/types";
import { pidLabel } from "../chat/artifacts";

type Block =
  | { kind: "heading"; level: number; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "list"; ordered: boolean; start: number; items: { text: string; depth: number }[] }
  | { kind: "quote"; text: string }
  | { kind: "code"; text: string }
  | { kind: "rule" }
  | { kind: "table"; head: string[]; rows: string[][] };

const FENCE = /^\s*(```|~~~)/;
const HEADING = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
const RULE = /^\s{0,3}([-*_])(?:\s*\1){2,}\s*$/;
const QUOTE = /^\s{0,3}>\s?(.*)$/;
const ITEM = /^(\s*)([-*+•]|\d{1,4}[.)])\s+(.*)$/;
const TABLE_SEP = /^\s*\|?\s*:?-{3,}:?\s*(?:\|\s*:?-{3,}:?\s*)*\|?\s*$/;
const DOWNLOAD = /^\s*\[\[download:[^\]]*\]\]\s*$/;

function cells(line: string): string[] {
  let t = line.trim();
  if (t.startsWith("|")) t = t.slice(1);
  if (t.endsWith("|")) t = t.slice(0, -1);
  return t.split("|").map((c) => c.trim());
}

export function parseBlocks(markdown: string): Block[] {
  const lines = markdown.replace(/\r\n?/g, "\n").split("\n");
  const blocks: Block[] = [];
  let para: string[] = [];
  const flush = (): void => {
    if (para.length) blocks.push({ kind: "paragraph", text: para.join(" ") });
    para = [];
  };
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] as string;
    if (FENCE.test(line)) {
      flush();
      const fence = (FENCE.exec(line) as RegExpExecArray)[1] as string;
      const body: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] as string).trim().startsWith(fence)) body.push(lines[i++] as string);
      i++;
      blocks.push({ kind: "code", text: body.join("\n") });
      continue;
    }
    if (!line.trim() || DOWNLOAD.test(line)) {
      flush();
      i++;
      continue;
    }
    if (RULE.test(line)) {
      flush();
      blocks.push({ kind: "rule" });
      i++;
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      flush();
      blocks.push({ kind: "heading", level: (heading[1] as string).length, text: heading[2] as string });
      i++;
      continue;
    }
    if (line.includes("|") && i + 1 < lines.length && TABLE_SEP.test(lines[i + 1] as string)) {
      flush();
      const head = cells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && (lines[i] as string).includes("|") && (lines[i] as string).trim()) rows.push(cells(lines[i++] as string));
      blocks.push({ kind: "table", head, rows });
      continue;
    }
    if (QUOTE.test(line)) {
      flush();
      const body: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i] as string)) body.push((QUOTE.exec(lines[i++] as string) as RegExpExecArray)[1] as string);
      blocks.push({ kind: "quote", text: body.join(" ") });
      continue;
    }
    const item = ITEM.exec(line);
    if (item) {
      flush();
      const ordered = /\d/.test(item[2] as string);
      const list: Block & { kind: "list" } = { kind: "list", ordered, start: ordered ? Number.parseInt(item[2] as string, 10) : 1, items: [] };
      while (i < lines.length) {
        const current = lines[i] as string;
        const m = ITEM.exec(current);
        if (m && /\d/.test(m[2] as string) === ordered) {
          list.items.push({ text: m[3] as string, depth: Math.min(3, Math.floor((m[1] as string).replace(/\t/g, "  ").length / 2)) });
          i++;
        } else if (!m && current.trim() && /^\s{2,}/.test(current) && list.items.length) {
          const last = list.items[list.items.length - 1] as { text: string };
          last.text += ` ${current.trim()}`;
          i++;
        } else break;
      }
      blocks.push(list);
      continue;
    }
    para.push(line.trim());
    i++;
  }
  flush();
  return blocks;
}

// ---- inline --------------------------------------------------------------------------------------------------

export interface InlineContext {
  onJump?: (pid: string) => void;
  citations?: readonly JobCitation[];
}

type Rule = { re: RegExp; render: (m: RegExpExecArray, ctx: InlineContext, key: string) => ReactNode };

const SAFE_URL = /^https?:\/\/[^\s<>"']+$/i;
const PID = "(?:p|fn|en|hf)\\d+(?:\\.\\d+)?";

const RULES: readonly Rule[] = [
  { re: /`([^`\n]+)`/, render: (m, _c, k) => <code key={k}>{m[1]}</code> },
  {
    re: new RegExp(`¶\\s?(${PID})(?:\\s?[–—-]\\s?¶?\\s?((?:p|fn|en|hf)?\\d+(?:\\.\\d+)?))?`),
    render: (m, ctx, k) => {
      const pid = m[1] as string;
      const end = m[2] ? `–${pidLabel(/^\d/.test(m[2]) ? `p${m[2]}` : m[2]).replace(/^¶/, "")}` : "";
      const label = `${pidLabel(pid)}${end}`;
      if (!ctx.onJump) return <span key={k} className="pid-ref">{label}</span>;
      const jump = ctx.onJump;
      return (
        <button key={k} type="button" className="pid-ref pid-ref--link" onClick={() => jump(pid)} title="Show this in the document">
          {label}
        </button>
      );
    },
  },
  {
    re: /\[((?:[SDW]\d{1,3})(?:\s*,\s*[SDW]\d{1,3})*)\]/,
    render: (m, ctx, k) => {
      const markers = (m[1] as string).split(/\s*,\s*/);
      return (
        <sup key={k} className="cite">
          {markers.map((marker, j) => {
            const num = Number(marker.slice(1));
            const citation = marker.startsWith("S") ? ctx.citations?.find((c) => c.num === num) : undefined;
            const label = marker.startsWith("S") ? String(num) : marker;
            return citation?.url ? (
              <a key={j} className="cite__chip" href={citation.url} target="_blank" rel="noopener noreferrer" title={citation.title}>
                {label}
              </a>
            ) : (
              <span key={j} className="cite__chip" title={citation?.title ?? marker}>
                {label}
              </span>
            );
          })}
        </sup>
      );
    },
  },
  {
    re: /\[([^\]\n]{1,300})\]\(([^()\s]{1,2000})\)/,
    render: (m, ctx, k) => {
      const href = m[2] as string;
      const children = inline(m[1] as string, ctx, `${k}.`);
      return SAFE_URL.test(href) ? (
        <a key={k} href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      ) : (
        <Fragment key={k}>{children}</Fragment>
      );
    },
  },
  { re: /\*\*(?=\S)([^\n]*?\S)\*\*/, render: (m, ctx, k) => <strong key={k}>{inline(m[1] as string, ctx, `${k}.`)}</strong> },
  { re: /__(?=\S)([^\n]*?\S)__/, render: (m, ctx, k) => <strong key={k}>{inline(m[1] as string, ctx, `${k}.`)}</strong> },
  {
    re: /(?<![\p{L}\p{N}*])\*(?=[^\s*])([^*\n]*?[^\s*])\*(?![\p{L}\p{N}*])/u,
    render: (m, ctx, k) => <em key={k}>{inline(m[1] as string, ctx, `${k}.`)}</em>,
  },
  {
    re: /(?<![\p{L}\p{N}_])_(?=[^\s_])([^_\n]*?[^\s_])_(?![\p{L}\p{N}_])/u,
    render: (m, ctx, k) => <em key={k}>{inline(m[1] as string, ctx, `${k}.`)}</em>,
  },
];

function inline(text: string, ctx: InlineContext, keyPrefix = ""): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  let n = 0;
  while (rest) {
    let best: { m: RegExpExecArray; rule: Rule } | null = null;
    for (const rule of RULES) {
      const m = rule.re.exec(rest);
      if (m && (best === null || m.index < best.m.index)) best = { m, rule };
    }
    if (best === null) {
      out.push(rest);
      break;
    }
    if (best.m.index > 0) out.push(rest.slice(0, best.m.index));
    out.push(best.rule.render(best.m, ctx, `${keyPrefix}${n++}`));
    rest = rest.slice(best.m.index + best.m[0].length);
  }
  return out;
}

export function Inline({ text, ...ctx }: { text: string } & InlineContext) {
  return <>{inline(text, ctx)}</>;
}

// ---- blocks --------------------------------------------------------------------------------------------------

function renderList(block: Block & { kind: "list" }, ctx: InlineContext, key: number): ReactNode {
  const Tag = block.ordered ? "ol" : "ul";
  return (
    <Tag key={key} {...(block.ordered && block.start !== 1 ? { start: block.start } : {})}>
      {block.items.map((item, j) => (
        <li key={j} className={item.depth ? `depth-${item.depth}` : undefined}>
          {inline(item.text, ctx)}
        </li>
      ))}
    </Tag>
  );
}

function renderBlock(block: Block, ctx: InlineContext, key: number): ReactNode {
  switch (block.kind) {
    case "heading": {
      const Tag = block.level <= 2 ? "h3" : "h4";
      return <Tag key={key}>{inline(block.text, ctx)}</Tag>;
    }
    case "paragraph":
      return <p key={key}>{inline(block.text, ctx)}</p>;
    case "list":
      return renderList(block, ctx, key);
    case "quote":
      return <blockquote key={key}>{inline(block.text, ctx)}</blockquote>;
    case "code":
      return (
        <pre key={key}>
          <code>{block.text}</code>
        </pre>
      );
    case "rule":
      return <hr key={key} />;
    case "table":
      return (
        <div key={key} className="md-table">
          <table>
            <thead>
              <tr>
                {block.head.map((h, j) => (
                  <th key={j}>{inline(h, ctx)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {block.head.map((_h, j) => (
                    <td key={j}>{inline(row[j] ?? "", ctx)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

export function Markdown({ text, className, ...ctx }: { text: string; className?: string } & InlineContext) {
  const blocks = useMemo(() => parseBlocks(text), [text]);
  return <div className={className ?? "md"}>{blocks.map((b, i) => renderBlock(b, ctx, i))}</div>;
}
